import type { FollowupDraft } from "@/components/prompt-input/submit"
import { coworkExecutionContext, executionContextPrefix } from "./execution-context"

export type CoworkLanguageSettings = {
  applicationLocale: string
  reportOutputLanguage: string
}

export function coworkLanguageInstructions(settings: CoworkLanguageSettings) {
  return [
    "Current-turn language settings:",
    `application_locale: ${settings.applicationLocale}`,
    `reply_language: ${settings.applicationLocale}`,
    `report_output_language: ${settings.reportOutputLanguage}`,
    "Use reply_language for conversational replies, clarification questions and options, progress explanations, and completion summaries.",
    "Use report_output_language independently for generated report content, titles, headings, notes, previews, and downloadable files.",
    "An explicit request to change report language changes reports only, not chat replies. Only an explicit request to change chat reply language overrides reply_language.",
    "The language of the user's task text, jurisdiction, report template names, source documents, or these execution instructions must not implicitly change either language setting.",
    "Preserve original source evidence, fixed statutory labels, machine-readable schema keys, enum values, and export codes.",
    "These current-turn settings take precedence over older conversation or session language settings.",
  ].join("\n")
}

export function coworkDraftInstructions(draft: FollowupDraft) {
  if (!draft.language && !draft.executionContext) return
  const text = draft.prompt
    .map((part) => ("content" in part && !("synthetic" in part && part.synthetic) ? part.content : ""))
    .join("")
  const context = coworkExecutionContext(text, {
    ...draft.executionContext,
    hints: {
      ...draft.executionContext?.hints,
      ...(draft.language ? {
        application_locale: draft.language.applicationLocale,
        reply_language: draft.language.applicationLocale,
        report_output_language: draft.language.reportOutputLanguage,
      } : {}),
    },
  })
  return [
    ...(draft.language ? [coworkLanguageInstructions(draft.language)] : []),
    executionContextPrefix + JSON.stringify(context),
  ].join("\n")
}

export function injectCoworkLanguage(draft: FollowupDraft): FollowupDraft {
  const content = coworkDraftInstructions(draft)
  if (!content) return draft
  return {
    ...draft,
    prompt: [
      { type: "text", content, start: 0, end: content.length, synthetic: true },
      ...draft.prompt.filter(
        (part) =>
          !(part.type === "text" && part.synthetic && (
            part.content.startsWith("Current-turn language settings:\n") ||
            part.content.startsWith(executionContextPrefix)
          )),
      ),
    ],
  }
}
